/**
 * useDraftAutosave — persists arbitrary form state to localStorage
 * under a stable key, so accidental tab close / refresh doesn't lose
 * unsaved work. Restores on next mount.
 *
 * Usage:
 *   const [draft, setDraft, restored] = useDraftAutosave('wrapper-new', {
 *     slug: '', tools: [],
 *   });
 *
 * `restored === true` after the initial restore IFF the persisted value
 * differs from the supplied `initial`. A previously-saved-then-equal-to-
 * default state never trips the banner — that prevents the "Draft
 * restored" alert from firing on a freshly-cleared form just because
 * the autosave wrote an identical-to-default snapshot a few seconds
 * ago.
 *
 * The debounced writer is also paused for a short window after clear()
 * so the navigate-after-submit flow can't re-save the form's
 * still-mounted state right after clearing it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useDraftAutosave<T>(
  key: string,
  initial: T,
  delayMs = 500,
): [T, (next: T) => void, boolean, () => void] {
  const storageKey = `sumcp_draft_${key}`;
  const initialJson = JSON.stringify(initial);

  // Read once on mount so SSR + initial render line up.
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return initial;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  });

  // Banner state — only true when the persisted value differs from
  // `initial`. Stale-but-default-equal storage entries don't qualify.
  const [restored, setRestored] = useState(false);
  const restoredOnce = useRef(false);
  useEffect(() => {
    if (restoredOnce.current) return;
    restoredOnce.current = true;
    if (typeof window === 'undefined') return;
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return;
    if (raw === initialJson) {
      // Default-equal — clean up so we don't keep evaluating it on
      // every page visit.
      window.localStorage.removeItem(storageKey);
      return;
    }
    setRestored(true);
    // The hook only flags "real" restorations now.
  }, [storageKey, initialJson]);

  // Debounced write — but skipped during the "post-clear" window so a
  // form can navigate away after submit without re-saving its fields.
  const writePausedUntil = useRef<number>(0);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const t = window.setTimeout(() => {
      if (Date.now() < writePausedUntil.current) return;
      const json = JSON.stringify(value);
      // Don't bother persisting state that equals the default — saves
      // localStorage churn AND avoids a stale "draft" reappearing on
      // the next visit.
      if (json === initialJson) {
        window.localStorage.removeItem(storageKey);
        return;
      }
      try {
        window.localStorage.setItem(storageKey, json);
      } catch {
        /* localStorage quota exceeded — ignore */
      }
    }, delayMs);
    return () => window.clearTimeout(t);
  }, [storageKey, value, delayMs, initialJson]);

  const clear = useCallback(() => {
    if (typeof window !== 'undefined') window.localStorage.removeItem(storageKey);
    // Suppress the next debounced write — without this the form's
    // current state (whatever the user just saved) would be re-stored
    // 500ms later, defeating the clear.
    writePausedUntil.current = Date.now() + delayMs * 4;
    setRestored(false);
  }, [storageKey, delayMs]);

  return [value, setValue, restored, clear];
}
